// An iOS screenshot is saved at the device's exact native screen resolution, so its pixel
// size identifies it. Photos of a screen, Android screenshots, and images that were
// resized/compressed (e.g. forwarded on WhatsApp) don't match these sizes.
// This is a client-side check only: it can be bypassed, so the backend/manual review must still verify.

// Portrait width x height, in pixels.
const IPHONE_SCREEN_SIZES: [number, number][] = [
  [640, 960],   // iPhone 4/4s
  [640, 1136],  // iPhone 5/5s/5c/SE (1st gen); 6/7/8 with Display Zoom
  [750, 1334],  // iPhone 6/6s/7/8, SE (2nd/3rd gen)
  [1242, 2208], // iPhone 6/7/8 Plus
  [1125, 2001], // iPhone 6/7/8 Plus with Display Zoom
  [1125, 2436], // iPhone X/XS/11 Pro
  [828, 1792],  // iPhone XR/11
  [1242, 2688], // iPhone XS Max/11 Pro Max
  [1080, 2340], // iPhone 12 mini/13 mini
  [1170, 2532], // iPhone 12/12 Pro/13/13 Pro/14/16e
  [1284, 2778], // iPhone 12 Pro Max/13 Pro Max/14 Plus
  [1179, 2556], // iPhone 14 Pro/15/15 Pro/16
  [1290, 2796], // iPhone 14 Pro Max/15 Plus/15 Pro Max/16 Plus
  [1206, 2622], // iPhone 16 Pro/17/17 Pro
  [1320, 2868], // iPhone 16 Pro Max/17 Pro Max
  [1260, 2736], // iPhone Air
];

const IPAD_SCREEN_SIZES: [number, number][] = [
  [768, 1024], [1536, 2048], [1620, 2160], [1640, 2360], [1668, 2224], [1668, 2388],
  [2048, 2732], [1488, 2266], [1640, 2360], [1668, 2420], [2064, 2752],
];

const IOS_SCREEN_SIZES = [...IPHONE_SCREEN_SIZES, ...IPAD_SCREEN_SIZES];

/** True if width x height (either orientation) is a native iPhone/iPad screen resolution. */
export const isIosScreenSize = (width: number, height: number): boolean =>
  IOS_SCREEN_SIZES.some(([w, h]) => (width === w && height === h) || (width === h && height === w));

export type ScreenshotCheck = { ok: true } | { ok: false; reason: string };

const readImageSize = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('unreadable image'));
    };
    img.src = url;
  });

export const NOT_IOS_SCREENSHOT_MESSAGE =
  "This doesn't look like an iPhone screenshot. Open the app, press the Side button and Volume Up together, then upload that screenshot from Photos.";

export const checkIosScreenshot = async (file: File): Promise<ScreenshotCheck> => {
  if (!file.type.startsWith('image/')) {
    return { ok: false, reason: 'Please upload an image (PNG or JPG).' };
  }
  try {
    const { width, height } = await readImageSize(file);
    return isIosScreenSize(width, height) ? { ok: true } : { ok: false, reason: NOT_IOS_SCREENSHOT_MESSAGE };
  } catch {
    return { ok: false, reason: NOT_IOS_SCREENSHOT_MESSAGE };
  }
};

// localStorage holds ~5 MB per site, and a full-size iPhone PNG screenshot (2-6 MB, ~33% bigger
// as base64) doesn't fit. Re-encode as JPEG at the same pixel size so it fits and stays readable.
const MAX_DATA_URL_LENGTH = 3_000_000;

export const compressScreenshot = (file: File): Promise<{ dataUrl: string; type: string; name: string }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas not supported'));
      ctx.fillStyle = '#fff'; // JPEG has no transparency
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      let dataUrl = '';
      for (const quality of [0.85, 0.7, 0.55, 0.4]) {
        dataUrl = canvas.toDataURL('image/jpeg', quality);
        if (dataUrl.length <= MAX_DATA_URL_LENGTH) break;
      }
      resolve({ dataUrl, type: 'image/jpeg', name: file.name.replace(/\.[^.]+$/, '') + '.jpg' });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read file'));
    };
    img.src = url;
  });
